import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { TrendingUp, TrendingDown, DollarSign, Package, Users, Calendar, Download, Filter, Info, X } from "lucide-react";
import { toast } from "sonner";
import { SidebarTrigger } from "@/components/ui/sidebar";
import { useInternationalization } from "@/contexts/InternationalizationContext";
import { useQuery } from "@tanstack/react-query";
import { listOrders } from "@/integrations/supabase/orders";
import { listQuotes } from "@/integrations/supabase/quotes";
import { useOnboardingHint } from "@/hooks/useOnboardingHint";
import { supabase } from "@/integrations/supabase/client";
import { getCurrentEmpresaId } from "@/integrations/supabase/auth-utils";
import { listarVendasPorPeriodo, gerarEstatisticasVendas } from "@/integrations/supabase/sales-reports";

interface ReportData {
  period: string;
  totalRevenue: number;
  totalOrders: number;
  averageOrderValue: number;
  totalCosts: number;
  profit: number;
  profitMargin: number;
  topProducts: Array<{ name: string; count: number; revenue: number }>;
  topCustomers: Array<{ name: string; orders: number; revenue: number }>;
  statusBreakdown: Record<string, number>;
}

export default function Relatorios() {
  const navigate = useNavigate();
  const [selectedPeriod, setSelectedPeriod] = useState<string>("30");
  const { formatCurrency } = useInternationalization();
  const [reportType, setReportType] = useState<string>("overview");
  const { showHint: showOnboardingHint, hideHint: dismissOnboardingHint } = useOnboardingHint("relatorios");
  const [isRecebimentosDataExpanded, setIsRecebimentosDataExpanded] = useState(false);

  // Novos estados para relatórios customizados
  const [selectedReport, setSelectedReport] = useState<string>("recebimentos-data");

  // Redirecionar para consertos pendentes
  const handleReportChange = (value: string) => {
    if (value === "consertos-pendentes") {
      navigate("/relatorio-consertos");
    } else {
      setSelectedReport(value);
    }
  };
  const [selectedDate, setSelectedDate] = useState<string>(new Date().toISOString().split('T')[0]);
  const [vendaDate, setVendaDate] = useState<string>(new Date().toISOString().split('T')[0]);
  const [paymentPeriodStart, setPaymentPeriodStart] = useState<string>(new Date().toISOString().split('T')[0]);
  const [paymentPeriodEnd, setPaymentPeriodEnd] = useState<string>(new Date().toISOString().split('T')[0]);
  const [recebimentoPeriodStart, setRecebimentoPeriodStart] = useState<string>(new Date().toISOString().split('T')[0]);
  const [recebimentoPeriodEnd, setRecebimentoPeriodEnd] = useState<string>(new Date().toISOString().split('T')[0]);
  const [vendaPeriodStart, setVendaPeriodStart] = useState<string>(new Date().toISOString().split('T')[0]);
  const [vendaPeriodEnd, setVendaPeriodEnd] = useState<string>(new Date().toISOString().split('T')[0]);

  const { data: orders = [] } = useQuery({
    queryKey: ["orders"],
    queryFn: listOrders,
  });

  const { data: quotes = [] } = useQuery({
    queryKey: ["quotes"],
    queryFn: listQuotes,
  });

  const { data: contasPagar = [] } = useQuery({
    queryKey: ["contas_pagar", selectedPeriod],
    queryFn: async () => {
      try {
        const empresaId = await getCurrentEmpresaId();
        if (!empresaId) return [];

        const now = new Date();
        const daysAgo = parseInt(selectedPeriod);
        const startDate = new Date(now.getTime() - daysAgo * 24 * 60 * 60 * 1000);

        const { data, error } = await supabase
          .from('contas_pagar')
          .select('valor_pago, status, data_pagamento, created_at')
          .eq('empresa_id', empresaId)
          .eq('status', 'pago')
          .gte('created_at', startDate.toISOString());

        return error ? [] : (data || []);
      } catch {
        return [];
      }
    },
  });

  // Buscar recebimentos por data específica
  const { data: recebimentosPorData = [] } = useQuery({
    queryKey: ["recebimentos_por_data", selectedDate],
    queryFn: async () => {
      try {
        const empresaId = await getCurrentEmpresaId();
        if (!empresaId) return [];

        const { data, error } = await supabase
          .from('recebimentos_pedidos')
          .select('order_id, valor_recebido, data_recebimento, observacoes')
          .eq('empresa_id', empresaId)
          .gte('data_recebimento', selectedDate)
          .lt('data_recebimento', new Date(new Date(selectedDate).getTime() + 86400000).toISOString().split('T')[0]);

        return error ? [] : (data || []);
      } catch {
        return [];
      }
    },
  });

  // Buscar contas a pagar por período
  const { data: contasApagarPeriodo = [] } = useQuery({
    queryKey: ["contas_pagar_periodo", paymentPeriodStart, paymentPeriodEnd],
    queryFn: async () => {
      try {
        const empresaId = await getCurrentEmpresaId();
        if (!empresaId) return [];

        const { data, error } = await supabase
          .from('contas_pagar')
          .select('descricao, valor_total, valor_pago, data_vencimento, status')
          .eq('empresa_id', empresaId)
          .gte('data_vencimento', paymentPeriodStart)
          .lte('data_vencimento', paymentPeriodEnd);

        return error ? [] : (data || []);
      } catch {
        return [];
      }
    },
  });

  // Buscar recebimentos por período
  const { data: recebimentosPeriodo = [] } = useQuery({
    queryKey: ["recebimentos_periodo", paymentPeriodStart, paymentPeriodEnd],
    queryFn: async () => {
      try {
        const empresaId = await getCurrentEmpresaId();
        if (!empresaId) return [];

        const { data, error } = await supabase
          .from('recebimentos_pedidos')
          .select('order_id, valor_recebido, data_recebimento, observacoes')
          .eq('empresa_id', empresaId)
          .gte('data_recebimento', paymentPeriodStart)
          .lte('data_recebimento', paymentPeriodEnd);

        return error ? [] : (data || []);
      } catch {
        return [];
      }
    },
  });

  // Buscar vendas por data específica
  const { data: vendaDeData = [] } = useQuery({
    queryKey: ["vendas_por_data", vendaDate],
    queryFn: async () => {
      try {
        return await listarVendasPorPeriodo(vendaDate, vendaDate);
      } catch {
        return [];
      }
    },
  });

  // Buscar vendas por período
  const { data: vendasPeriodo = [] } = useQuery({
    queryKey: ["vendas_periodo", vendaPeriodStart, vendaPeriodEnd],
    queryFn: async () => {
      try {
        return await listarVendasPorPeriodo(vendaPeriodStart, vendaPeriodEnd);
      } catch {
        return [];
      }
    },
  });

  const reportData = useMemo((): ReportData => {
    const now = new Date();
    const daysAgo = parseInt(selectedPeriod);
    const startDate = new Date(now.getTime() - daysAgo * 24 * 60 * 60 * 1000);

    // Helper para calcular valor com desconto
    const getOrderValueWithDiscount = (order: any) => {
      const subtotal = Number(order.value || 0);
      const discount = Number((order as any)?.discount || 0);
      const discountType = (order as any)?.discount_type || 'fixed';
      const discountAmount = discountType === 'percentage'
        ? (subtotal * discount / 100)
        : discount;
      return Math.max(0, subtotal - discountAmount);
    };

    // Filtrar dados do período
    const filteredOrders = orders.filter(order => {
      const orderDate = new Date(order.created_at || order.delivery_date || '');
      return orderDate >= startDate && order.status !== 'Cancelado'; // Excluir pedidos cancelados
    });

    const filteredQuotes = quotes.filter(quote => {
      const quoteDate = new Date(quote.date || quote.created_at || '');
      return quoteDate >= startDate;
    });

    // Calcular métricas
    const totalRevenue = filteredOrders.reduce((sum, order) => sum + getOrderValueWithDiscount(order), 0);
    const totalOrders = filteredOrders.length;
    const averageOrderValue = totalOrders > 0 ? totalRevenue / totalOrders : 0;

    // Custos reais do período
    const totalCosts = contasPagar.reduce((sum, conta) => sum + (Number(conta.valor_pago) || 0), 0);
    const profit = totalRevenue - totalCosts;
    const profitMargin = totalRevenue > 0 ? (profit / totalRevenue) * 100 : 0;

    // Produtos mais vendidos
    const productCount: Record<string, { count: number; revenue: number }> = {};
    filteredOrders.forEach(order => {
      const productName = order.type || 'Produto';
      if (!productCount[productName]) {
        productCount[productName] = { count: 0, revenue: 0 };
      }
      productCount[productName].count += 1;
      productCount[productName].revenue += getOrderValueWithDiscount(order);
    });

    const topProducts = Object.entries(productCount)
      .map(([name, data]) => ({ name, ...data }))
      .sort((a, b) => b.revenue - a.revenue)
      .slice(0, 5);

    // Clientes que mais compram
    const customerCount: Record<string, { orders: number; revenue: number }> = {};
    filteredOrders.forEach(order => {
      const customerName = order.customer_name || 'Cliente';
      if (!customerCount[customerName]) {
        customerCount[customerName] = { orders: 0, revenue: 0 };
      }
      customerCount[customerName].orders += 1;
      customerCount[customerName].revenue += getOrderValueWithDiscount(order);
    });

    const topCustomers = Object.entries(customerCount)
      .map(([name, data]) => ({ name, ...data }))
      .sort((a, b) => b.revenue - a.revenue)
      .slice(0, 5);

    // Breakdown por status
    const statusBreakdown: Record<string, number> = {};
    filteredOrders.forEach(order => {
      const status = order.status || 'Desconhecido';
      statusBreakdown[status] = (statusBreakdown[status] || 0) + 1;
    });

    return {
      period: `${daysAgo} dias`,
      totalRevenue,
      totalOrders,
      averageOrderValue,
      totalCosts,
      profit,
      profitMargin,
      topProducts,
      topCustomers,
      statusBreakdown
    };
  }, [orders, quotes, selectedPeriod, contasPagar]);

  const exportReport = () => {
    console.log("=== GERANDO RELATÓRIO FINANCEIRO ===");
    console.log("Report Data:", reportData);
    
    // Gerar HTML do relatório
    const reportHtml = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>Relatório Financeiro - Ateliê Pro</title>
          <meta charset="utf-8">
          <style>
            * { margin: 0; padding: 0; box-sizing: border-box; }
            body { 
              font-family: 'Arial', sans-serif; 
              line-height: 1.6; 
              color: #333; 
              background: white; 
              padding: 20px; 
            }
            .container { max-width: 800px; margin: 0 auto; }
            .header { 
              text-align: center; 
              margin-bottom: 40px; 
              border-bottom: 3px solid #7c3aed; 
              padding-bottom: 20px; 
            }
            .header h1 { font-size: 28px; font-weight: bold; margin: 0; margin-bottom: 10px; color: #7c3aed; }
            .header .subtitle { font-size: 16px; color: #6b7280; }
            .section { 
              margin-bottom: 30px; 
              background: #f8fafc;
              padding: 20px;
              border-radius: 8px;
              border-left: 4px solid #7c3aed;
            }
            .section h2 { 
              font-size: 20px; 
              font-weight: bold; 
              margin-bottom: 15px; 
              color: #7c3aed; 
            }
            .grid { 
              display: grid; 
              grid-template-columns: 1fr 1fr; 
              gap: 20px; 
              margin-bottom: 20px; 
            }
            .item { 
              display: flex; 
              flex-direction: column; 
            }
            .label { 
              font-size: 12px; 
              color: #6b7280; 
              font-weight: bold; 
              text-transform: uppercase; 
              margin-bottom: 5px; 
            }
            .value { 
              font-size: 16px; 
              color: #1f2937; 
              font-weight: 500; 
            }
            .metric-card {
              background: white;
              padding: 15px;
              border-radius: 8px;
              border: 1px solid #e5e7eb;
              margin-bottom: 15px;
            }
            .metric-title {
              font-size: 14px;
              color: #6b7280;
              margin-bottom: 5px;
            }
            .metric-value {
              font-size: 24px;
              font-weight: bold;
              color: #059669;
            }
            .list-item {
              display: flex;
              justify-content: space-between;
              align-items: center;
              padding: 10px;
              background: white;
              border-radius: 6px;
              margin-bottom: 8px;
              border: 1px solid #e5e7eb;
            }
            .list-item .name {
              font-weight: 500;
              color: #1f2937;
            }
            .list-item .value {
              font-weight: bold;
              color: #059669;
            }
            .footer { 
              margin-top: 40px; 
              text-align: center; 
              font-size: 12px; 
              color: #6b7280; 
              border-top: 1px solid #e5e7eb; 
              padding-top: 20px; 
            }
            @media print {
              body { padding: 0; }
              .container { max-width: none; }
            }
          </style>
        </head>
        <body>
          <div class="container">
            <div class="header">
              <h1>📊 RELATÓRIO FINANCEIRO</h1>
              <div class="subtitle">Ateliê Pro - Sistema de Gestão</div>
              <div class="subtitle">Período: ${reportData.period}</div>
              <div class="subtitle">Gerado em: ${new Date().toLocaleDateString('pt-BR')}</div>
            </div>
            
            <div class="section">
              <h2>💰 Resumo Financeiro</h2>
              <div class="grid">
                <div class="metric-card">
                  <div class="metric-title">Receita Total</div>
                  <div class="metric-value">{formatCurrency(reportData.totalRevenue)}</div>
                </div>
                <div class="metric-card">
                  <div class="metric-title">Total de Pedidos</div>
                  <div class="metric-value">${reportData.totalOrders}</div>
                </div>
                <div class="metric-card">
                  <div class="metric-title">Ticket Médio</div>
                  <div class="metric-value">{formatCurrency(reportData.averageOrderValue)}</div>
                </div>
                <div class="metric-card">
                  <div class="metric-title">Clientes Ativos</div>
                  <div class="metric-value">${reportData.topCustomers.length}</div>
                </div>
              </div>
            </div>
            
            <div class="section">
              <h2>📈 Análise de Rentabilidade</h2>
              <div class="grid">
                <div class="metric-card" style="background: #f0fdf4; border-left: 4px solid #059669;">
                  <div class="metric-title">Margem de Lucro</div>
                  <div class="metric-value" style="color: #059669;">${reportData.profitMargin.toFixed(1)}%</div>
                  <div style="font-size: 12px; color: #059669; margin-top: 5px;">
                    {formatCurrency(reportData.profit)} lucro real
                  </div>
                </div>
                <div class="metric-card" style="background: #eff6ff; border-left: 4px solid #3b82f6;">
                  <div class="metric-title">Custos Reais</div>
                  <div class="metric-value" style="color: #3b82f6;">{formatCurrency(reportData.totalCosts)}</div>
                  <div style="font-size: 12px; color: #3b82f6; margin-top: 5px;">
                    ${reportData.totalRevenue > 0 ? ((reportData.totalCosts / reportData.totalRevenue) * 100).toFixed(1) : '0'}% da receita
                  </div>
                </div>
                <div class="metric-card" style="background: #faf5ff; border-left: 4px solid #7c3aed;">
                  <div class="metric-title">ROI Médio</div>
                  <div class="metric-value" style="color: #7c3aed;">${reportData.totalOrders > 0 ? ((reportData.profit / reportData.totalOrders) * 100 / reportData.averageOrderValue).toFixed(1) : '0'}%</div>
                  <div style="font-size: 12px; color: #7c3aed; margin-top: 5px;">
                    Lucro por pedido
                  </div>
                </div>
              </div>
            </div>
            
            <div class="section">
              <h2>📦 Produtos Mais Vendidos</h2>
              ${reportData.topProducts.map((product, index) => `
                <div class="list-item">
                  <div>
                    <div class="name">${index + 1}. ${product.name}</div>
                    <div style="font-size: 12px; color: #6b7280;">${product.count} vendas</div>
                  </div>
                  <div class="value">{formatCurrency(product.revenue)}</div>
                </div>
              `).join('')}
            </div>
            
            <div class="section">
              <h2>👥 Clientes que Mais Compram</h2>
              ${reportData.topCustomers.map((customer, index) => `
                <div class="list-item">
                  <div>
                    <div class="name">${index + 1}. ${customer.name}</div>
                    <div style="font-size: 12px; color: #6b7280;">${customer.orders} pedidos</div>
                  </div>
                  <div class="value">{formatCurrency(customer.revenue)}</div>
                </div>
              `).join('')}
            </div>
            
            <div class="section">
              <h2>📈 Status dos Pedidos</h2>
              ${Object.entries(reportData.statusBreakdown).map(([status, count]) => `
                <div class="list-item">
                  <div class="name">${status}</div>
                  <div class="value">${count} pedidos</div>
                </div>
              `).join('')}
            </div>
            
            <div class="footer">
              <p>Relatório gerado automaticamente pelo Ateliê Pro</p>
              <p>Data: ${new Date().toLocaleString('pt-BR')}</p>
            </div>
          </div>
        </body>
      </html>
    `;
    
    // Abrir nova janela com o relatório
    const newWindow = window.open('', '_blank');
    if (newWindow) {
      newWindow.document.write(reportHtml);
      newWindow.document.close();
      
      // Aguardar carregamento e abrir diálogo de impressão
      newWindow.onload = () => {
        console.log("Relatório carregado, abrindo diálogo de impressão...");
        newWindow.print();
      };
      
      // Mostrar notificação de sucesso
      toast.success("Relatório gerado com sucesso! Abrindo para impressão...");
    } else {
      toast.error("Não foi possível abrir a janela. Verifique se os pop-ups estão bloqueados.");
    }
  };

  const exportRecebimentosPorPeriodo = () => {
    const totalRecebido = recebimentosPeriodo.reduce((sum, item: any) => sum + (Number(item.valor_recebido) || 0), 0);
    const dataInicio = new Date(recebimentoPeriodStart + 'T00:00:00').toLocaleDateString('pt-BR');
    const dataFim = new Date(recebimentoPeriodEnd + 'T00:00:00').toLocaleDateString('pt-BR');

    const reportHtml = `
      <!DOCTYPE html>
      <html lang="pt-BR">
        <head>
          <meta charset="UTF-8">
          <meta name="viewport" content="width=device-width, initial-scale=1.0">
          <title>Relatório de Recebimentos por Período</title>
          <style>
            * { margin: 0; padding: 0; box-sizing: border-box; }
            body { font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; background: #f8f9fa; color: #2c3e50; line-height: 1.6; }
            .container { max-width: 900px; margin: 0 auto; background: white; padding: 50px 40px; }
            .header-gradient { background: linear-gradient(135deg, #7c3aed 0%, #a855f7 100%); color: white; padding: 40px; border-radius: 12px 12px 0 0; text-align: center; margin: -50px -40px 0 -40px; }
            .header-gradient h1 { font-size: 32px; margin-bottom: 10px; font-weight: 700; letter-spacing: -0.5px; }
            .header-gradient .brand { font-size: 14px; opacity: 0.95; margin-bottom: 20px; }
            .header-gradient .date-info { font-size: 18px; font-weight: 600; margin-top: 15px; }
            .header-info { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; margin: 30px 0; }
            .info-box { background: linear-gradient(135deg, #f3e8ff 0%, #ede9fe 100%); padding: 20px; border-radius: 8px; border-left: 4px solid #7c3aed; }
            .info-label { font-size: 12px; font-weight: 600; color: #6b21a8; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 8px; }
            .info-value { font-size: 24px; font-weight: 700; color: #7c3aed; }
            .section { margin: 35px 0; }
            .section-title { font-size: 18px; font-weight: 700; color: #2c3e50; margin-bottom: 20px; padding-bottom: 12px; border-bottom: 3px solid #7c3aed; }
            .items-container { background: #f8f9fa; border-radius: 8px; overflow: hidden; }
            .item { display: flex; justify-content: space-between; align-items: center; padding: 16px 20px; border-bottom: 1px solid #e5e7eb; }
            .item:last-child { border-bottom: none; }
            .item-info { flex: 1; }
            .item-desc { font-weight: 500; color: #2c3e50; }
            .item-date { font-size: 12px; color: #999; margin-top: 4px; }
            .item-value { font-weight: 700; color: #7c3aed; font-size: 16px; min-width: 130px; text-align: right; }
            .total-section { background: linear-gradient(135deg, #7c3aed 0%, #a855f7 100%); color: white; padding: 25px 20px; border-radius: 8px; margin-top: 20px; display: flex; justify-content: space-between; align-items: center; }
            .total-label { font-size: 16px; font-weight: 600; }
            .total-value { font-size: 28px; font-weight: 700; }
            .empty { text-align: center; color: #999; padding: 60px 20px; font-size: 16px; }
            .footer { margin-top: 50px; padding-top: 20px; border-top: 1px solid #e5e7eb; text-align: center; font-size: 11px; color: #999; }
            .footer-brand { font-weight: 600; color: #7c3aed; margin-bottom: 5px; }
            @media print {
              body { background: white; padding: 0; }
              .container { margin: 0; padding: 50px 40px; }
              * { -webkit-print-color-adjust: exact !important; color-adjust: exact !important; print-color-adjust: exact !important; }
            }
          </style>
        </head>
        <body>
          <div class="container">
            <div class="header-gradient">
              <h1>💳 Recebimentos por Período</h1>
              <div class="brand">Ateliê Pro - Sistema de Gestão Criativa</div>
              <div class="date-info">De ${dataInicio} até ${dataFim}</div>
            </div>

            <div class="header-info">
              <div class="info-box">
                <div class="info-label">Total Recebido</div>
                <div class="info-value">${formatCurrency(totalRecebido)}</div>
              </div>
              <div class="info-box">
                <div class="info-label">Quantidade</div>
                <div class="info-value">${recebimentosPeriodo.length}</div>
              </div>
            </div>

            <div class="section">
              ${recebimentosPeriodo.length > 0
                ? `<div class="section-title">📊 Detalhes dos Recebimentos</div>
                   <div class="items-container">
                     ${recebimentosPeriodo.map((item: any, idx: number) => `
                       <div class="item">
                         <div class="item-info">
                           <div class="item-desc"><strong>${idx + 1}.</strong> Pedido ${item.order_id}</div>
                           <div class="item-date">${new Date(item.data_recebimento + 'T00:00:00').toLocaleDateString('pt-BR')}</div>
                         </div>
                         <div class="item-value">${formatCurrency(Number(item.valor_recebido) || 0)}</div>
                       </div>
                     `).join('')}
                   </div>
                   <div class="total-section">
                     <div class="total-label">TOTAL RECEBIDO</div>
                     <div class="total-value">${formatCurrency(totalRecebido)}</div>
                   </div>`
                : '<div class="empty">📭 Nenhum recebimento registrado neste período</div>'
              }
            </div>

            <div class="footer">
              <div class="footer-brand">Ateliê Pro</div>
              <p>Relatório gerado em ${new Date().toLocaleDateString('pt-BR')} às ${new Date().toLocaleTimeString('pt-BR')}</p>
            </div>
          </div>
        </body>
      </html>
    `;

    const newWindow = window.open('', '_blank');
    if (newWindow) {
      newWindow.document.write(reportHtml);
      newWindow.document.close();
      newWindow.onload = () => {
        newWindow.print();
      };
      toast.success("Relatório de recebimentos por período gerado com sucesso!");
    } else {
      toast.error("Não foi possível abrir a janela. Verifique se os pop-ups estão bloqueados.");
    }
  };

  const exportRecebimentosPorData = () => {
    const totalRecebido = recebimentosPorData.reduce((sum, item: any) => sum + (Number(item.valor_recebido) || 0), 0);
    const dataFormatada = new Date(selectedDate + 'T00:00:00').toLocaleDateString('pt-BR', {
      weekday: 'long',
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });

    const reportHtml = `
      <!DOCTYPE html>
      <html lang="pt-BR">
        <head>
          <meta charset="UTF-8">
          <meta name="viewport" content="width=device-width, initial-scale=1.0">
          <title>Relatório de Recebimentos</title>
          <style>
            * { margin: 0; padding: 0; box-sizing: border-box; }
            body { font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; background: #f8f9fa; color: #2c3e50; line-height: 1.6; }
            .container { max-width: 900px; margin: 0 auto; background: white; padding: 50px 40px; }
            .header-gradient { background: linear-gradient(135deg, #7c3aed 0%, #a855f7 100%); color: white; padding: 40px; border-radius: 12px 12px 0 0; text-align: center; margin: -50px -40px 0 -40px; }
            .header-gradient h1 { font-size: 32px; margin-bottom: 10px; font-weight: 700; letter-spacing: -0.5px; }
            .header-gradient .brand { font-size: 14px; opacity: 0.95; margin-bottom: 20px; }
            .header-gradient .date-info { font-size: 18px; font-weight: 600; margin-top: 15px; }
            .header-info { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; margin: 30px 0; }
            .info-box { background: linear-gradient(135deg, #f3e8ff 0%, #ede9fe 100%); padding: 20px; border-radius: 8px; border-left: 4px solid #7c3aed; }
            .info-label { font-size: 12px; font-weight: 600; color: #6b21a8; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 8px; }
            .info-value { font-size: 24px; font-weight: 700; color: #7c3aed; }
            .section { margin: 35px 0; }
            .section-title { font-size: 18px; font-weight: 700; color: #2c3e50; margin-bottom: 20px; padding-bottom: 12px; border-bottom: 3px solid #7c3aed; }
            .items-container { background: #f8f9fa; border-radius: 8px; overflow: hidden; }
            .item { display: flex; justify-content: space-between; align-items: center; padding: 16px 20px; border-bottom: 1px solid #e5e7eb; }
            .item:last-child { border-bottom: none; }
            .item-desc { flex: 1; font-weight: 500; color: #2c3e50; }
            .item-value { font-weight: 700; color: #7c3aed; font-size: 16px; min-width: 130px; text-align: right; }
            .total-section { background: linear-gradient(135deg, #7c3aed 0%, #a855f7 100%); color: white; padding: 25px 20px; border-radius: 8px; margin-top: 20px; display: flex; justify-content: space-between; align-items: center; }
            .total-label { font-size: 16px; font-weight: 600; }
            .total-value { font-size: 28px; font-weight: 700; }
            .empty { text-align: center; color: #999; padding: 60px 20px; font-size: 16px; }
            .footer { margin-top: 50px; padding-top: 20px; border-top: 1px solid #e5e7eb; text-align: center; font-size: 11px; color: #999; }
            .footer-brand { font-weight: 600; color: #7c3aed; margin-bottom: 5px; }
            @media print {
              body { background: white; padding: 0; }
              .container { margin: 0; padding: 50px 40px; }
              * { -webkit-print-color-adjust: exact !important; color-adjust: exact !important; print-color-adjust: exact !important; }
            }
          </style>
        </head>
        <body>
          <div class="container">
            <div class="header-gradient">
              <h1>💳 Recebimentos do Dia</h1>
              <div class="brand">Ateliê Pro - Sistema de Gestão Criativa</div>
              <div class="date-info">${dataFormatada}</div>
            </div>

            <div class="header-info">
              <div class="info-box">
                <div class="info-label">Total Recebido</div>
                <div class="info-value">${formatCurrency(totalRecebido)}</div>
              </div>
              <div class="info-box">
                <div class="info-label">Quantidade</div>
                <div class="info-value">${recebimentosPorData.length}</div>
              </div>
            </div>

            <div class="section">
              ${recebimentosPorData.length > 0
                ? `<div class="section-title">📊 Detalhes dos Recebimentos</div>
                   <div class="items-container">
                     ${recebimentosPorData.map((item: any, idx: number) => `
                       <div class="item">
                         <div class="item-desc"><strong>${idx + 1}.</strong> ${item.descricao || 'Recebimento'}</div>
                         <div class="item-value">${formatCurrency(Number(item.valor_recebido) || 0)}</div>
                       </div>
                     `).join('')}
                   </div>
                   <div class="total-section">
                     <div class="total-label">TOTAL RECEBIDO</div>
                     <div class="total-value">${formatCurrency(totalRecebido)}</div>
                   </div>`
                : '<div class="empty">📭 Nenhum recebimento registrado para esta data</div>'
              }
            </div>

            <div class="footer">
              <div class="footer-brand">Ateliê Pro</div>
              <p>Relatório gerado em ${new Date().toLocaleDateString('pt-BR')} às ${new Date().toLocaleTimeString('pt-BR')}</p>
            </div>
          </div>
        </body>
      </html>
    `;

    const newWindow = window.open('', '_blank');
    if (newWindow) {
      newWindow.document.write(reportHtml);
      newWindow.document.close();
      newWindow.onload = () => {
        newWindow.print();
      };
      toast.success("Relatório de recebimentos gerado com sucesso!");
    } else {
      toast.error("Não foi possível abrir a janela. Verifique se os pop-ups estão bloqueados.");
    }
  };

  const exportContasApagarPeriodo = () => {
    const totalAPagar = contasApagarPeriodo.reduce((sum: number, item: any) => sum + (Number(item.valor_total) || 0), 0);
    const totalPago = contasApagarPeriodo.reduce((sum: number, item: any) => sum + (Number(item.valor_pago) || 0), 0);
    const saldoAberto = totalAPagar - totalPago;
    const dataInicio = new Date(paymentPeriodStart + 'T00:00:00').toLocaleDateString('pt-BR');
    const dataFim = new Date(paymentPeriodEnd + 'T00:00:00').toLocaleDateString('pt-BR');

    const reportHtml = `
      <!DOCTYPE html>
      <html lang="pt-BR">
        <head>
          <meta charset="UTF-8">
          <meta name="viewport" content="width=device-width, initial-scale=1.0">
          <title>Relatório de Contas a Pagar</title>
          <style>
            * { margin: 0; padding: 0; box-sizing: border-box; }
            body { font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; background: #f8f9fa; color: #2c3e50; line-height: 1.6; }
            .container { max-width: 900px; margin: 0 auto; background: white; padding: 50px 40px; }
            .header-gradient { background: linear-gradient(135deg, #7c3aed 0%, #a855f7 100%); color: white; padding: 40px; border-radius: 12px 12px 0 0; text-align: center; margin: -50px -40px 0 -40px; }
            .header-gradient h1 { font-size: 32px; margin-bottom: 10px; font-weight: 700; letter-spacing: -0.5px; }
            .header-gradient .brand { font-size: 14px; opacity: 0.95; margin-bottom: 20px; }
            .header-gradient .date-info { font-size: 18px; font-weight: 600; margin-top: 15px; }
            .header-info { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 20px; margin: 30px 0; }
            .info-box { background: linear-gradient(135deg, #f3e8ff 0%, #ede9fe 100%); padding: 20px; border-radius: 8px; border-left: 4px solid #7c3aed; }
            .info-label { font-size: 12px; font-weight: 600; color: #6b21a8; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 8px; }
            .info-value { font-size: 24px; font-weight: 700; color: #7c3aed; }
            .section { margin: 35px 0; }
            .section-title { font-size: 18px; font-weight: 700; color: #2c3e50; margin-bottom: 20px; padding-bottom: 12px; border-bottom: 3px solid #7c3aed; }
            .items-container { background: #f8f9fa; border-radius: 8px; overflow: hidden; }
            .item { display: grid; grid-template-columns: 1fr 150px 150px; gap: 15px; align-items: center; padding: 16px 20px; border-bottom: 1px solid #e5e7eb; }
            .item:last-child { border-bottom: none; }
            .item-desc { font-weight: 500; color: #2c3e50; }
            .item-status { font-size: 12px; color: #999; margin-top: 4px; }
            .value-box { text-align: right; }
            .value-label { font-size: 11px; color: #999; font-weight: 600; margin-bottom: 4px; }
            .item-value { font-weight: 700; color: #7c3aed; font-size: 16px; }
            .item-value.paid { color: #10b981; }
            .stats-row { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 15px; margin-top: 20px; }
            .stat-box { background: linear-gradient(135deg, #f3e8ff 0%, #ede9fe 100%); padding: 20px; border-radius: 8px; border-left: 4px solid #7c3aed; }
            .stat-label { font-size: 12px; font-weight: 600; color: #6b21a8; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 8px; }
            .stat-value { font-size: 22px; font-weight: 700; color: #7c3aed; }
            .stat-box.open { border-left-color: #f59e0b; }
            .stat-box.open .stat-label { color: #b45309; }
            .stat-box.open .stat-value { color: #f59e0b; }
            .empty { text-align: center; color: #999; padding: 60px 20px; font-size: 16px; }
            .footer { margin-top: 50px; padding-top: 20px; border-top: 1px solid #e5e7eb; text-align: center; font-size: 11px; color: #999; }
            .footer-brand { font-weight: 600; color: #7c3aed; margin-bottom: 5px; }
            @media print {
              body { background: white; padding: 0; }
              .container { margin: 0; padding: 50px 40px; }
              * { -webkit-print-color-adjust: exact !important; color-adjust: exact !important; print-color-adjust: exact !important; }
            }
          </style>
        </head>
        <body>
          <div class="container">
            <div class="header-gradient">
              <h1>💰 Contas a Pagar</h1>
              <div class="brand">Ateliê Pro - Sistema de Gestão Criativa</div>
              <div class="date-info">De ${dataInicio} até ${dataFim}</div>
            </div>

            <div class="header-info">
              <div class="info-box">
                <div class="info-label">Total a Pagar</div>
                <div class="info-value">${formatCurrency(totalAPagar)}</div>
              </div>
              <div class="info-box">
                <div class="info-label">Total Pago</div>
                <div class="info-value">${formatCurrency(totalPago)}</div>
              </div>
              <div class="info-box">
                <div class="info-label">Saldo Aberto</div>
                <div class="info-value">${formatCurrency(saldoAberto)}</div>
              </div>
            </div>

            <div class="section">
              ${contasApagarPeriodo.length > 0
                ? `<div class="section-title">📋 Detalhes das Contas</div>
                   <div class="items-container">
                     <div style="display: grid; grid-template-columns: 1fr 150px 150px; gap: 15px; padding: 12px 20px; background: #f3e8ff; font-weight: 600; color: #6b21a8; font-size: 12px;">
                       <div>DESCRIÇÃO</div>
                       <div style="text-align: right;">A PAGAR</div>
                       <div style="text-align: right;">PAGO</div>
                     </div>
                     ${contasApagarPeriodo.map((item: any, idx: number) => `
                       <div class="item">
                         <div>
                           <div class="item-desc"><strong>${idx + 1}.</strong> ${item.descricao || 'Conta'}</div>
                           <div class="item-status">Status: ${item.status === 'pago' ? '✅ Pago' : '⏳ Pendente'}</div>
                         </div>
                         <div class="value-box">
                           <div class="item-value">${formatCurrency(Number(item.valor_total) || 0)}</div>
                         </div>
                         <div class="value-box">
                           <div class="item-value paid">${formatCurrency(Number(item.valor_pago) || 0)}</div>
                         </div>
                       </div>
                     `).join('')}
                   </div>
                   <div class="stats-row">
                     <div class="stat-box">
                       <div class="stat-label">Total a Pagar</div>
                       <div class="stat-value">${formatCurrency(totalAPagar)}</div>
                     </div>
                     <div class="stat-box">
                       <div class="stat-label">Total Pago</div>
                       <div class="stat-value">${formatCurrency(totalPago)}</div>
                     </div>
                     <div class="stat-box open">
                       <div class="stat-label">Saldo em Aberto</div>
                       <div class="stat-value">${formatCurrency(saldoAberto)}</div>
                     </div>
                   </div>`
                : '<div class="empty">📭 Nenhuma conta a pagar neste período</div>'
              }
            </div>

            <div class="footer">
              <div class="footer-brand">Ateliê Pro</div>
              <p>Relatório gerado em ${new Date().toLocaleDateString('pt-BR')} às ${new Date().toLocaleTimeString('pt-BR')}</p>
            </div>
          </div>
        </body>
      </html>
    `;

    const newWindow = window.open('', '_blank');
    if (newWindow) {
      newWindow.document.write(reportHtml);
      newWindow.document.close();
      newWindow.onload = () => {
        newWindow.print();
      };
      toast.success("Relatório de contas a pagar gerado com sucesso!");
    } else {
      toast.error("Não foi possível abrir a janela. Verifique se os pop-ups estão bloqueados.");
    }
  };

  const exportVendasPorData = () => {
    const totalVendido = vendaDeData.reduce((sum, item: any) => sum + (Number(item.order_value) || 0), 0);
    const ticketMedio = vendaDeData.length > 0 ? totalVendido / vendaDeData.length : 0;
    const dataFormatada = new Date(vendaDate + 'T00:00:00').toLocaleDateString('pt-BR', {
      weekday: 'long',
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });

    const reportHtml = `
      <!DOCTYPE html>
      <html lang="pt-BR">
        <head>
          <meta charset="UTF-8">
          <meta name="viewport" content="width=device-width, initial-scale=1.0">
          <title>Relatório de Vendas</title>
          <style>
            * { margin: 0; padding: 0; box-sizing: border-box; }
            body { font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; background: #f8f9fa; color: #2c3e50; line-height: 1.6; }
            .container { max-width: 900px; margin: 0 auto; background: white; padding: 50px 40px; }
            .header-gradient { background: linear-gradient(135deg, #7c3aed 0%, #a855f7 100%); color: white; padding: 40px; border-radius: 12px 12px 0 0; text-align: center; margin: -50px -40px 0 -40px; }
            .header-gradient h1 { font-size: 32px; margin-bottom: 10px; font-weight: 700; letter-spacing: -0.5px; }
            .header-gradient .brand { font-size: 14px; opacity: 0.95; margin-bottom: 20px; }
            .header-gradient .date-info { font-size: 18px; font-weight: 600; margin-top: 15px; }
            .header-info { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 20px; margin: 30px 0; }
            .info-box { background: linear-gradient(135deg, #f3e8ff 0%, #ede9fe 100%); padding: 20px; border-radius: 8px; border-left: 4px solid #7c3aed; }
            .info-label { font-size: 12px; font-weight: 600; color: #6b21a8; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 8px; }
            .info-value { font-size: 24px; font-weight: 700; color: #7c3aed; }
            .section { margin: 35px 0; }
            .section-title { font-size: 18px; font-weight: 700; color: #2c3e50; margin-bottom: 20px; padding-bottom: 12px; border-bottom: 3px solid #7c3aed; }
            .items-container { background: #f8f9fa; border-radius: 8px; overflow: hidden; }
            .item { display: flex; flex-direction: column; padding: 16px 20px; border-bottom: 1px solid #e5e7eb; }
            .item:last-child { border-bottom: none; }
            .item-header { display: flex; justify-content: space-between; align-items: start; }
            .item-desc { font-weight: 600; color: #2c3e50; }
            .item-detail { font-size: 12px; color: #999; margin-top: 4px; }
            .item-value { font-weight: 700; color: #7c3aed; font-size: 18px; }
            .total-section { background: linear-gradient(135deg, #7c3aed 0%, #a855f7 100%); color: white; padding: 25px 20px; border-radius: 8px; margin-top: 20px; display: flex; justify-content: space-between; align-items: center; }
            .total-label { font-size: 16px; font-weight: 600; }
            .total-value { font-size: 28px; font-weight: 700; }
            .empty { text-align: center; color: #999; padding: 60px 20px; font-size: 16px; }
            .footer { margin-top: 50px; padding-top: 20px; border-top: 1px solid #e5e7eb; text-align: center; font-size: 11px; color: #999; }
            .footer-brand { font-weight: 600; color: #7c3aed; margin-bottom: 5px; }
            @media print {
              body { background: white; padding: 0; }
              .container { margin: 0; padding: 50px 40px; }
              * { -webkit-print-color-adjust: exact !important; color-adjust: exact !important; print-color-adjust: exact !important; }
            }
          </style>
        </head>
        <body>
          <div class="container">
            <div class="header-gradient">
              <h1>📊 Vendas do Dia</h1>
              <div class="brand">Ateliê Pro - Sistema de Gestão Criativa</div>
              <div class="date-info">${dataFormatada}</div>
            </div>

            <div class="header-info">
              <div class="info-box">
                <div class="info-label">Total de Vendas</div>
                <div class="info-value">${formatCurrency(totalVendido)}</div>
              </div>
              <div class="info-box">
                <div class="info-label">Quantidade</div>
                <div class="info-value">${vendaDeData.length}</div>
              </div>
              <div class="info-box">
                <div class="info-label">Ticket Médio</div>
                <div class="info-value">${formatCurrency(ticketMedio)}</div>
              </div>
            </div>

            <div class="section">
              ${vendaDeData.length > 0
                ? `<div class="section-title">📋 Detalhes das Vendas</div>
                   <div class="items-container">
                     ${vendaDeData.map((item: any, idx: number) => `
                       <div class="item">
                         <div class="item-header">
                           <div>
                             <div class="item-desc"><strong>${idx + 1}.</strong> Pedido ${item.order_code}</div>
                             <div class="item-detail">${item.customer_name} | Tipo: ${item.order_type} | Status: ${item.status}</div>
                           </div>
                           <div class="item-value">${formatCurrency(Number(item.order_value) || 0)}</div>
                         </div>
                       </div>
                     `).join('')}
                   </div>
                   <div class="total-section">
                     <div class="total-label">TOTAL DE VENDAS</div>
                     <div class="total-value">${formatCurrency(totalVendido)}</div>
                   </div>`
                : '<div class="empty">📭 Nenhuma venda registrada para esta data</div>'
              }
            </div>

            <div class="footer">
              <div class="footer-brand">Ateliê Pro</div>
              <p>Relatório gerado em ${new Date().toLocaleDateString('pt-BR')} às ${new Date().toLocaleTimeString('pt-BR')}</p>
            </div>
          </div>
        </body>
      </html>
    `;

    const newWindow = window.open('', '_blank');
    if (newWindow) {
      newWindow.document.write(reportHtml);
      newWindow.document.close();
      newWindow.onload = () => {
        newWindow.print();
      };
      toast.success("Relatório de vendas gerado com sucesso!");
    } else {
      toast.error("Não foi possível abrir a janela. Verifique se os pop-ups estão bloqueados.");
    }
  };

  const exportVendasPorPeriodo = () => {
    const totalVendido = vendasPeriodo.reduce((sum, item: any) => sum + (Number(item.order_value) || 0), 0);
    const ticketMedio = vendasPeriodo.length > 0 ? totalVendido / vendasPeriodo.length : 0;
    const dataInicio = new Date(vendaPeriodStart + 'T00:00:00').toLocaleDateString('pt-BR');
    const dataFim = new Date(vendaPeriodEnd + 'T00:00:00').toLocaleDateString('pt-BR');

    const reportHtml = `
      <!DOCTYPE html>
      <html lang="pt-BR">
        <head>
          <meta charset="UTF-8">
          <meta name="viewport" content="width=device-width, initial-scale=1.0">
          <title>Relatório de Vendas por Período</title>
          <style>
            * { margin: 0; padding: 0; box-sizing: border-box; }
            body { font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; background: #f8f9fa; color: #2c3e50; line-height: 1.6; }
            .container { max-width: 900px; margin: 0 auto; background: white; padding: 50px 40px; }
            .header-gradient { background: linear-gradient(135deg, #7c3aed 0%, #a855f7 100%); color: white; padding: 40px; border-radius: 12px 12px 0 0; text-align: center; margin: -50px -40px 0 -40px; }
            .header-gradient h1 { font-size: 32px; margin-bottom: 10px; font-weight: 700; letter-spacing: -0.5px; }
            .header-gradient .brand { font-size: 14px; opacity: 0.95; margin-bottom: 20px; }
            .header-gradient .date-info { font-size: 18px; font-weight: 600; margin-top: 15px; }
            .header-info { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 20px; margin: 30px 0; }
            .info-box { background: linear-gradient(135deg, #f3e8ff 0%, #ede9fe 100%); padding: 20px; border-radius: 8px; border-left: 4px solid #7c3aed; }
            .info-label { font-size: 12px; font-weight: 600; color: #6b21a8; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 8px; }
            .info-value { font-size: 24px; font-weight: 700; color: #7c3aed; }
            .section { margin: 35px 0; }
            .section-title { font-size: 18px; font-weight: 700; color: #2c3e50; margin-bottom: 20px; padding-bottom: 12px; border-bottom: 3px solid #7c3aed; }
            .items-container { background: #f8f9fa; border-radius: 8px; overflow: hidden; }
            .item { display: flex; flex-direction: column; padding: 16px 20px; border-bottom: 1px solid #e5e7eb; }
            .item:last-child { border-bottom: none; }
            .item-header { display: flex; justify-content: space-between; align-items: start; }
            .item-desc { font-weight: 600; color: #2c3e50; }
            .item-detail { font-size: 12px; color: #999; margin-top: 4px; }
            .item-value { font-weight: 700; color: #7c3aed; font-size: 18px; }
            .total-section { background: linear-gradient(135deg, #7c3aed 0%, #a855f7 100%); color: white; padding: 25px 20px; border-radius: 8px; margin-top: 20px; display: flex; justify-content: space-between; align-items: center; }
            .total-label { font-size: 16px; font-weight: 600; }
            .total-value { font-size: 28px; font-weight: 700; }
            .empty { text-align: center; color: #999; padding: 60px 20px; font-size: 16px; }
            .footer { margin-top: 50px; padding-top: 20px; border-top: 1px solid #e5e7eb; text-align: center; font-size: 11px; color: #999; }
            .footer-brand { font-weight: 600; color: #7c3aed; margin-bottom: 5px; }
            @media print {
              body { background: white; padding: 0; }
              .container { margin: 0; padding: 50px 40px; }
              * { -webkit-print-color-adjust: exact !important; color-adjust: exact !important; print-color-adjust: exact !important; }
            }
          </style>
        </head>
        <body>
          <div class="container">
            <div class="header-gradient">
              <h1>📊 Vendas por Período</h1>
              <div class="brand">Ateliê Pro - Sistema de Gestão Criativa</div>
              <div class="date-info">De ${dataInicio} até ${dataFim}</div>
            </div>

            <div class="header-info">
              <div class="info-box">
                <div class="info-label">Total de Vendas</div>
                <div class="info-value">${formatCurrency(totalVendido)}</div>
              </div>
              <div class="info-box">
                <div class="info-label">Quantidade</div>
                <div class="info-value">${vendasPeriodo.length}</div>
              </div>
              <div class="info-box">
                <div class="info-label">Ticket Médio</div>
                <div class="info-value">${formatCurrency(ticketMedio)}</div>
              </div>
            </div>

            <div class="section">
              ${vendasPeriodo.length > 0
                ? `<div class="section-title">📋 Detalhes das Vendas</div>
                   <div class="items-container">
                     ${vendasPeriodo.map((item: any, idx: number) => `
                       <div class="item">
                         <div class="item-header">
                           <div>
                             <div class="item-desc"><strong>${idx + 1}.</strong> Pedido ${item.order_code}</div>
                             <div class="item-detail">${item.customer_name} | Tipo: ${item.order_type} | Status: ${item.status} | ${new Date(item.created_at).toLocaleDateString('pt-BR')}</div>
                           </div>
                           <div class="item-value">${formatCurrency(Number(item.order_value) || 0)}</div>
                         </div>
                       </div>
                     `).join('')}
                   </div>
                   <div class="total-section">
                     <div class="total-label">TOTAL DE VENDAS</div>
                     <div class="total-value">${formatCurrency(totalVendido)}</div>
                   </div>`
                : '<div class="empty">📭 Nenhuma venda registrada neste período</div>'
              }
            </div>

            <div class="footer">
              <div class="footer-brand">Ateliê Pro</div>
              <p>Relatório gerado em ${new Date().toLocaleDateString('pt-BR')} às ${new Date().toLocaleTimeString('pt-BR')}</p>
            </div>
          </div>
        </body>
      </html>
    `;

    const newWindow = window.open('', '_blank');
    if (newWindow) {
      newWindow.document.write(reportHtml);
      newWindow.document.close();
      newWindow.onload = () => {
        newWindow.print();
      };
      toast.success("Relatório de vendas por período gerado com sucesso!");
    } else {
      toast.error("Não foi possível abrir a janela. Verifique se os pop-ups estão bloqueados.");
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-blue-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-50 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-3 md:py-4 flex items-center justify-between animate-fade-in">
          <div className="flex items-center gap-3">
            <SidebarTrigger className="text-purple-600 hover:text-purple-700 transition-colors" />
            <div>
              <h1 className="text-xl md:text-2xl font-semibold text-gray-900 flex items-center gap-2">
                <TrendingUp className="w-6 h-6 text-purple-600" />
                Relatórios Financeiros
              </h1>
              <p className="text-gray-500 text-xs md:text-sm mt-0 font-medium">Análise de performance e vendas</p>
            </div>
          </div>

          <div className="flex gap-2">
            <Select value={selectedPeriod} onValueChange={setSelectedPeriod}>
              <SelectTrigger className="w-32 md:w-40 border-purple-200 hover:border-purple-300 transition-colors shadow-sm hover:shadow-md">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="7">Últimos 7 dias</SelectItem>
                <SelectItem value="30">Últimos 30 dias</SelectItem>
                <SelectItem value="90">Últimos 90 dias</SelectItem>
                <SelectItem value="365">Último ano</SelectItem>
              </SelectContent>
            </Select>
            <Button
              onClick={exportReport}
              variant="outline"
              className="gap-2 border-purple-200 hover:border-purple-400 hover:shadow-md transition-all duration-200"
            >
              <Download className="w-4 h-4" />
              <span className="hidden sm:inline">Exportar</span>
            </Button>
          </div>
        </div>
      </div>

      {showOnboardingHint && (
        <div className="max-w-6xl mx-auto px-4 pt-4">
          <Card className="border border-purple-200 bg-gradient-to-r from-purple-50 to-pink-50">
            <CardHeader className="pb-2 flex flex-row items-start justify-between gap-2">
              <div className="flex items-center gap-2">
                <Info className="w-4 h-4 text-purple-600" />
                <CardTitle className="text-sm font-semibold text-gray-900">
                  Enxergue seu ateliê como um negócio de verdade
                </CardTitle>
              </div>
              <button
                className="text-gray-400 hover:text-gray-600"
                onClick={dismissOnboardingHint}
                aria-label="Fechar dica de relatórios"
              >
                <X className="w-4 h-4" />
              </button>
            </CardHeader>
            <CardContent className="pt-0 text-xs md:text-sm text-gray-700 space-y-1">
              <p>
                Aqui você vê quanto está faturando, quais produtos mais saem e quais clientes mais compram.
                Use esses números para decidir onde focar sua energia.
              </p>
              <p>
                Uma boa prática é olhar os relatórios toda semana ou todo mês. Assim você não se surpreende
                e consegue ver se o ateliê está crescendo do jeito que você gostaria.
              </p>
            </CardContent>
          </Card>
        </div>
      )}

      <div className="max-w-7xl mx-auto px-3 md:px-4 py-2 md:py-3 space-y-3 md:space-y-4">
        {/* Seletor de Relatório Único */}
        <Card className="bg-gradient-to-br from-white to-purple-50 border-gray-200 shadow-sm hover:shadow-md transition-all duration-300 animate-fade-in overflow-hidden">
          <CardHeader className="bg-purple-50 border-b border-gray-200 px-3 md:px-4 py-2">
            <CardTitle className="text-sm md:text-base text-gray-900 flex items-center gap-2">
              <Filter className="w-4 h-4 text-purple-600" />
              Relatório
            </CardTitle>
          </CardHeader>
          <CardContent className="p-3 md:p-4 space-y-4">
            {/* Seletor de Tipo de Relatório */}
            <div className="animate-fade-in" style={{animationDelay: '0.1s'}}>
              <Label htmlFor="report-type" className="text-xs md:text-sm font-bold text-purple-900 block mb-1.5">Escolha o Relatório</Label>
              <Select value={selectedReport} onValueChange={handleReportChange}>
                <SelectTrigger id="report-type" className="border border-purple-300 hover:border-purple-400 focus:border-purple-500 focus:ring-1 focus:ring-purple-300/30 text-sm font-medium shadow-sm hover:shadow-md transition-all duration-200">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="shadow-lg">
                  <SelectItem value="recebimentos-data">💳 Recebimentos por Data</SelectItem>
                  <SelectItem value="recebimentos-periodo">💳 Recebimentos Período</SelectItem>
                  <SelectItem value="contas-pagar">💰 Contas a Pagar</SelectItem>
                  <SelectItem value="vendas-data">📊 Vendas por Data</SelectItem>
                  <SelectItem value="vendas-periodo">📊 Vendas Período</SelectItem>
                  <SelectItem value="consertos-pendentes">📋 Consertos Pendentes</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Campos de Data Dinâmicos */}
            <div className="grid grid-cols-2 gap-2 md:gap-3 animate-fade-in" style={{animationDelay: '0.2s'}}>
              {(selectedReport === "recebimentos-data" || selectedReport === "vendas-data") && (
                <div className="col-span-2 animate-fade-in">
                  <Label htmlFor="date-picker" className="text-sm font-bold text-purple-900 block mb-1.5">Data específica</Label>
                  <div className="flex gap-1.5 mb-1.5">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        const hoje = new Date().toISOString().split('T')[0];
                        selectedReport === "recebimentos-data" ? setSelectedDate(hoje) : setVendaDate(hoje);
                      }}
                      className="text-xs px-2 py-1 h-7 border-purple-200 text-purple-600 hover:bg-purple-50"
                    >
                      Hoje
                    </Button>
                  </div>
                  <Input
                    id="date-picker"
                    type="date"
                    value={selectedReport === "recebimentos-data" ? selectedDate : vendaDate}
                    onChange={(e) => selectedReport === "recebimentos-data" ? setSelectedDate(e.target.value) : setVendaDate(e.target.value)}
                    className="border border-purple-300 hover:border-purple-400 focus:border-purple-500 focus:ring-1 focus:ring-purple-300/30 shadow-sm hover:shadow-md transition-all duration-200 font-medium text-sm h-8"
                  />
                </div>
              )}

              {(selectedReport === "recebimentos-periodo" || selectedReport === "vendas-periodo" || selectedReport === "contas-pagar") && (
                <>
                  <div className="col-span-2 animate-fade-in">
                    <Label className="text-sm font-bold text-purple-900 block mb-2">Atalhos rápidos</Label>
                    <div className="flex gap-2 flex-wrap">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          const hoje = new Date().toISOString().split('T')[0];
                          if (selectedReport === "recebimentos-periodo") {
                            setRecebimentoPeriodStart(hoje);
                            setRecebimentoPeriodEnd(hoje);
                          } else if (selectedReport === "vendas-periodo") {
                            setVendaPeriodStart(hoje);
                            setVendaPeriodEnd(hoje);
                          } else {
                            setPaymentPeriodStart(hoje);
                            setPaymentPeriodEnd(hoje);
                          }
                        }}
                        className="text-xs border-purple-200 text-purple-600 hover:bg-purple-50"
                      >
                        Hoje
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          const hoje = new Date();
                          const inicioSemana = new Date(hoje.setDate(hoje.getDate() - hoje.getDay()));
                          const fimSemana = new Date(hoje.setDate(hoje.getDate() + 6));
                          const start = inicioSemana.toISOString().split('T')[0];
                          const end = fimSemana.toISOString().split('T')[0];
                          if (selectedReport === "recebimentos-periodo") {
                            setRecebimentoPeriodStart(start);
                            setRecebimentoPeriodEnd(end);
                          } else if (selectedReport === "vendas-periodo") {
                            setVendaPeriodStart(start);
                            setVendaPeriodEnd(end);
                          } else {
                            setPaymentPeriodStart(start);
                            setPaymentPeriodEnd(end);
                          }
                        }}
                        className="text-xs border-purple-200 text-purple-600 hover:bg-purple-50"
                      >
                        Esta Semana
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          const hoje = new Date();
                          const inicioMes = new Date(hoje.getFullYear(), hoje.getMonth(), 1);
                          const fimMes = new Date(hoje.getFullYear(), hoje.getMonth() + 1, 0);
                          const start = inicioMes.toISOString().split('T')[0];
                          const end = fimMes.toISOString().split('T')[0];
                          if (selectedReport === "recebimentos-periodo") {
                            setRecebimentoPeriodStart(start);
                            setRecebimentoPeriodEnd(end);
                          } else if (selectedReport === "vendas-periodo") {
                            setVendaPeriodStart(start);
                            setVendaPeriodEnd(end);
                          } else {
                            setPaymentPeriodStart(start);
                            setPaymentPeriodEnd(end);
                          }
                        }}
                        className="text-xs border-purple-200 text-purple-600 hover:bg-purple-50"
                      >
                        Este Mês
                      </Button>
                    </div>
                  </div>
                  <div className="animate-fade-in" style={{animationDelay: '0.1s'}}>
                    <Label htmlFor="date-start" className="text-sm font-bold text-purple-900 block mb-2">Data Inicial</Label>
                    <Input
                      id="date-start"
                      type="date"
                      value={
                        selectedReport === "recebimentos-periodo" ? recebimentoPeriodStart :
                        selectedReport === "vendas-periodo" ? vendaPeriodStart :
                        paymentPeriodStart
                      }
                      onChange={(e) => {
                        if (selectedReport === "recebimentos-periodo") setRecebimentoPeriodStart(e.target.value);
                        else if (selectedReport === "vendas-periodo") setVendaPeriodStart(e.target.value);
                        else setPaymentPeriodStart(e.target.value);
                      }}
                      className="border-2 border-purple-300 hover:border-purple-400 focus:border-purple-500 focus:ring-2 focus:ring-purple-300/30 shadow-md hover:shadow-lg transition-all duration-200 font-medium"
                    />
                  </div>
                  <div className="animate-fade-in" style={{animationDelay: '0.15s'}}>
                    <Label htmlFor="date-end" className="text-sm font-bold text-purple-900 block mb-2">Data Final</Label>
                    <Input
                      id="date-end"
                      type="date"
                      value={
                        selectedReport === "recebimentos-periodo" ? recebimentoPeriodEnd :
                        selectedReport === "vendas-periodo" ? vendaPeriodEnd :
                        paymentPeriodEnd
                      }
                      onChange={(e) => {
                        if (selectedReport === "recebimentos-periodo") setRecebimentoPeriodEnd(e.target.value);
                        else if (selectedReport === "vendas-periodo") setVendaPeriodEnd(e.target.value);
                        else setPaymentPeriodEnd(e.target.value);
                      }}
                      className="border-2 border-purple-300 hover:border-purple-400 focus:border-purple-500 focus:ring-2 focus:ring-purple-300/30 shadow-md hover:shadow-lg transition-all duration-200 font-medium"
                    />
                  </div>
                </>
              )}
            </div>

            {/* Resumo do Relatório */}
            <div className="bg-gradient-to-br from-purple-50 to-pink-50 p-3 md:p-4 rounded-lg border-l-4 border-purple-600 shadow-sm hover:shadow-md transition-all duration-300 animate-fade-in" style={{animationDelay: '0.3s'}}>
              <p className="text-xs font-bold text-purple-900 uppercase tracking-widest flex items-center gap-2 mb-1.5">
                📋 Resumo do Relatório
              </p>
              <p className="text-2xl md:text-3xl font-bold text-purple-600 mt-1 mb-2">
                {selectedReport === "recebimentos-data" && formatCurrency(recebimentosPorData.reduce((sum: number, item: any) => sum + (Number(item.valor_recebido) || 0), 0))}
                {selectedReport === "recebimentos-periodo" && formatCurrency(recebimentosPeriodo.reduce((sum: number, item: any) => sum + (Number(item.valor_recebido) || 0), 0))}
                {selectedReport === "contas-pagar" && formatCurrency(contasApagarPeriodo.reduce((sum: number, item: any) => sum + (Number(item.valor_total) || 0), 0))}
                {selectedReport === "vendas-data" && formatCurrency(vendaDeData.reduce((sum: number, item: any) => sum + (Number(item.order_value) || 0), 0))}
                {selectedReport === "vendas-periodo" && formatCurrency(vendasPeriodo.reduce((sum: number, item: any) => sum + (Number(item.order_value) || 0), 0))}
              </p>
              <p className="text-xs md:text-sm font-semibold text-purple-600 flex items-center gap-1.5">
                <span className="text-base">●</span>
                {selectedReport === "recebimentos-data" && `${recebimentosPorData.length} recebimento(s)`}
                {selectedReport === "recebimentos-periodo" && `${recebimentosPeriodo.length} recebimento(s)`}
                {selectedReport === "contas-pagar" && `${contasApagarPeriodo.length} conta(s)`}
                {selectedReport === "vendas-data" && `${vendaDeData.length} venda(s)`}
                {selectedReport === "vendas-periodo" && `${vendasPeriodo.length} venda(s)`}
              </p>
            </div>

            {/* Botão Imprimir */}
            <Button
              onClick={() => {
                if (selectedReport === "recebimentos-data") exportRecebimentosPorData();
                else if (selectedReport === "recebimentos-periodo") exportRecebimentosPorPeriodo();
                else if (selectedReport === "contas-pagar") exportContasApagarPeriodo();
                else if (selectedReport === "vendas-data") exportVendasPorData();
                else if (selectedReport === "vendas-periodo") exportVendasPorPeriodo();
              }}
              className="w-full h-10 md:h-11 text-sm md:text-base font-bold bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white shadow-md hover:shadow-lg transition-all duration-300 active:scale-95 rounded-lg"
            >
              <Download className="w-4 h-4 mr-1.5" />
              Gerar e Imprimir
            </Button>
          </CardContent>
        </Card>

        {/* Métricas Principais */}
        <div className="grid gap-2 md:gap-3 md:grid-cols-2 lg:grid-cols-4">
          <Card className="bg-gradient-to-br from-white to-green-50 border-gray-200 shadow-sm hover:shadow-md transition-all duration-300 animate-fade-in overflow-hidden" style={{animationDelay: '0.1s'}}>
            <CardContent className="pt-2 md:pt-3">
              <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Receita Total</p>
              <p className="text-base md:text-lg font-bold text-green-600 mt-0.5">
                {formatCurrency(reportData.totalRevenue)}
              </p>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-white to-blue-50 border-gray-200 shadow-sm hover:shadow-md transition-all duration-300 animate-fade-in overflow-hidden" style={{animationDelay: '0.2s'}}>
            <CardContent className="pt-2 md:pt-3">
              <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Pedidos</p>
              <p className="text-base md:text-lg font-bold text-blue-600 mt-0.5">
                {reportData.totalOrders}
              </p>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-white to-purple-50 border-gray-200 shadow-sm hover:shadow-md transition-all duration-300 animate-fade-in overflow-hidden" style={{animationDelay: '0.3s'}}>
            <CardContent className="pt-2 md:pt-3">
              <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Ticket Médio</p>
              <p className="text-base md:text-lg font-bold text-purple-600 mt-0.5">
                {formatCurrency(reportData.averageOrderValue)}
              </p>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-white to-orange-50 border-gray-200 shadow-sm hover:shadow-md transition-all duration-300 animate-fade-in overflow-hidden" style={{animationDelay: '0.4s'}}>
            <CardContent className="pt-2 md:pt-3">
              <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Clientes</p>
              <p className="text-base md:text-lg font-bold text-orange-600 mt-0.5">
                {reportData.topCustomers.length}
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Análise de Rentabilidade */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8 lg:gap-10 mb-8">
          <Card className="bg-gradient-to-br from-green-50 to-emerald-50 border-2 border-green-200 shadow-xl hover:shadow-2xl hover:shadow-green-500/30 transition-all duration-300 transform hover:scale-105 animate-fade-in" style={{animationDelay: '0.5s'}}>
            <CardContent className="p-4 md:p-6">
              <div className="flex items-center justify-between gap-2">
                <div className="flex-1">
                  <p className="text-xs font-bold text-green-700 uppercase tracking-wide">Margem de Lucro</p>
                  <p className="text-3xl font-bold text-green-600 mt-2">
                    {reportData.profitMargin.toFixed(1)}%
                  </p>
                  <p className="text-xs text-green-600 mt-2 font-semibold">
                    {formatCurrency(reportData.profit)} lucro real
                  </p>
                </div>
                <div className="p-3 bg-green-100 rounded-full">
                  <TrendingUp className="w-8 h-8 text-green-600 drop-shadow-lg" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-blue-50 to-cyan-50 border-2 border-blue-200 shadow-xl hover:shadow-2xl hover:shadow-blue-500/30 transition-all duration-300 transform hover:scale-105 animate-fade-in" style={{animationDelay: '0.6s'}}>
            <CardContent className="p-4 md:p-6">
              <div className="flex items-center justify-between gap-2">
                <div className="flex-1">
                  <p className="text-xs font-bold text-blue-700 uppercase tracking-wide">Custos Reais</p>
                  <p className="text-3xl font-bold text-blue-600 mt-2">
                    {formatCurrency(reportData.totalCosts)}
                  </p>
                  <p className="text-xs text-blue-600 mt-2 font-semibold">
                    {reportData.totalRevenue > 0 ? ((reportData.totalCosts / reportData.totalRevenue) * 100).toFixed(1) : '0'}% da receita
                  </p>
                </div>
                <div className="p-3 bg-blue-100 rounded-full">
                  <Package className="w-8 h-8 text-blue-600 drop-shadow-lg" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-purple-50 to-violet-50 border-2 border-purple-200 shadow-xl hover:shadow-2xl hover:shadow-purple-500/30 transition-all duration-300 transform hover:scale-105 animate-fade-in" style={{animationDelay: '0.7s'}}>
            <CardContent className="p-4 md:p-6">
              <div className="flex items-center justify-between gap-2">
                <div className="flex-1">
                  <p className="text-xs font-bold text-purple-700 uppercase tracking-wide">ROI Médio</p>
                  <p className="text-3xl font-bold text-purple-600 mt-2">
                    {reportData.totalOrders > 0 ? ((reportData.profit / reportData.totalOrders) * 100 / reportData.averageOrderValue).toFixed(1) : '0'}%
                  </p>
                  <p className="text-xs text-purple-600 mt-2 font-semibold">
                    Lucro por pedido
                  </p>
                </div>
                <div className="p-3 bg-purple-100 rounded-full">
                  <DollarSign className="w-8 h-8 text-purple-600 drop-shadow-lg" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="grid gap-6 md:gap-8 lg:gap-10 lg:grid-cols-2 mt-8 md:mt-10 lg:mt-12">
          {/* Produtos Mais Vendidos */}
          <Card className="bg-gradient-to-br from-white to-purple-50 border-purple-200 shadow-xl hover:shadow-2xl transition-all duration-300 animate-fade-in overflow-hidden" style={{animationDelay: '0.8s'}}>
            <CardHeader className="bg-gradient-to-r from-purple-600 to-purple-700 text-white px-6 py-2.5">
              <CardTitle className="flex items-center gap-2 text-base md:text-lg">
                <Package className="w-4 h-4" />
                Produtos Mais Vendidos
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-4">
              <div className="space-y-2">
                {reportData.topProducts.map((product, index) => (
                  <div key={product.name} className="flex items-center justify-between p-2.5 bg-gradient-to-r from-purple-50 to-pink-50 rounded-lg border-l-3 border-purple-500 hover:shadow-md transition-all duration-200">
                    <div className="flex items-center gap-2">
                      <div className="w-7 h-7 bg-gradient-to-br from-purple-500 to-pink-500 text-white rounded-full flex items-center justify-center font-bold text-xs shadow-md">
                        {index + 1}
                      </div>
                      <div>
                        <p className="font-semibold text-gray-900 text-sm">{product.name}</p>
                        <p className="text-xs text-gray-600">{product.count} vendas</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-green-600 text-sm">{formatCurrency(product.revenue)}</p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Clientes Top */}
          <Card className="bg-gradient-to-br from-white to-blue-50 border-blue-200 shadow-xl hover:shadow-2xl transition-all duration-300 animate-fade-in overflow-hidden" style={{animationDelay: '0.9s'}}>
            <CardHeader className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-6 py-2.5">
              <CardTitle className="flex items-center gap-2 text-base md:text-lg">
                <Users className="w-4 h-4" />
                Clientes que Mais Compram
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-4">
              <div className="space-y-2">
                {reportData.topCustomers.map((customer, index) => (
                  <div key={customer.name} className="flex items-center justify-between p-2.5 bg-gradient-to-r from-blue-50 to-cyan-50 rounded-lg border-l-3 border-blue-500 hover:shadow-md transition-all duration-200">
                    <div className="flex items-center gap-2">
                      <div className="w-7 h-7 bg-gradient-to-br from-blue-500 to-cyan-500 text-white rounded-full flex items-center justify-center font-bold text-xs shadow-md">
                        {index + 1}
                      </div>
                      <div>
                        <p className="font-semibold text-gray-900 text-sm">{customer.name}</p>
                        <p className="text-xs text-gray-600">{customer.orders} pedidos</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-green-600 text-sm">{formatCurrency(customer.revenue)}</p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Status dos Pedidos */}
        <Card className="bg-white border border-gray-200/50 shadow-sm">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calendar className="w-5 h-5 text-purple-600" />
              Status dos Pedidos
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
              {Object.entries(reportData.statusBreakdown).map(([status, count]) => (
                <div key={status} className="p-4 bg-gray-50 rounded-lg text-center">
                  <p className="text-2xl font-bold text-gray-900">{count}</p>
                  <p className="text-sm text-gray-600">{status}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Resumo do Período */}
        <Card className="bg-white border border-gray-200/50 shadow-sm">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-purple-600" />
              Resumo do Período
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid gap-6 md:grid-cols-3">
              <div className="text-center p-6 bg-green-50 rounded-lg">
                <TrendingUp className="w-8 h-8 text-green-600 mx-auto mb-2" />
                <p className="text-sm text-green-600 font-medium">Receita Total</p>
                <p className="text-2xl font-bold text-green-700">
                  {formatCurrency(reportData.totalRevenue)}
                </p>
                <p className="text-xs text-green-600 mt-1">em {reportData.period}</p>
              </div>
              
              <div className="text-center p-6 bg-blue-50 rounded-lg">
                <Package className="w-8 h-8 text-blue-600 mx-auto mb-2" />
                <p className="text-sm text-blue-600 font-medium">Pedidos Processados</p>
                <p className="text-2xl font-bold text-blue-700">
                  {reportData.totalOrders}
                </p>
                <p className="text-xs text-blue-600 mt-1">em {reportData.period}</p>
              </div>
              
              <div className="text-center p-6 bg-purple-50 rounded-lg">
                <DollarSign className="w-8 h-8 text-purple-600 mx-auto mb-2" />
                <p className="text-sm text-purple-600 font-medium">Ticket Médio</p>
                <p className="text-2xl font-bold text-purple-700">
                  {formatCurrency(reportData.averageOrderValue)}
                </p>
                <p className="text-xs text-purple-600 mt-1">por pedido</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
